import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { phappsRequest } from "@/services/apiService";
import {
  API_PATHS,
  buildGroupManagerLinkedChemistsPath,
} from "@/config/apiConstants";
import CreateGroupManagerModal from "@/components/modals/admin/CreateGroupManagerModal";
import EditGroupManagerModal from "@/components/modals/admin/EditGroupManagerModal";
import AddGroupManagerChemistsModal from "@/components/modals/admin/AddGroupManagerChemistsModal";
import useAlert from "@/components/feedback/useAlert";

function extractArray(data, keys) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  for (const k of keys) {
    if (Array.isArray(data[k])) return data[k];
  }
  return [];
}

function managerId(m) {
  return m?.Id ?? m?.id;
}

function chemistRowId(c) {
  return c?.ChemistID ?? c?.chemistID;
}

function BoolPill({ value }) {
  const on = !!value;
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
        on ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
      }`}
    >
      {on ? "Yes" : "No"}
    </span>
  );
}

function filterChemistRows(rows, q) {
  const s = q.trim().toLowerCase();
  if (!s) return rows;
  return rows.filter((r) => {
    const name = String(r.ChemistName ?? r.chemistName ?? "").toLowerCase();
    const email = String(r.Email ?? r.email ?? "").toLowerCase();
    return name.includes(s) || email.includes(s);
  });
}

const GroupManagersPage = () => {
  const { showAlert, AlertSnackbar } = useAlert();

  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(true);
  const [managersError, setManagersError] = useState(null);
  const [activeOnly, setActiveOnly] = useState(true);

  const [selectedManager, setSelectedManager] = useState(null);
  const [linkedChemists, setLinkedChemists] = useState([]);
  const [allChemists, setAllChemists] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [addChemistsOpen, setAddChemistsOpen] = useState(false);

  const [linkedSearch, setLinkedSearch] = useState("");
  const [linkedSelected, setLinkedSelected] = useState(() => new Set());
  const [addSaving, setAddSaving] = useState(false);
  const [removeSaving, setRemoveSaving] = useState(false);

  const visibleManagers = useMemo(() => {
    if (!activeOnly) return managers;
    return managers.filter((m) => m.IsActive !== false);
  }, [managers, activeOnly]);

  useEffect(() => {
    if (!activeOnly || !selectedManager) return;
    if (selectedManager.IsActive === false) setSelectedManager(null);
  }, [activeOnly, selectedManager]);

  const loadManagers = useCallback(async () => {
    setManagersLoading(true);
    setManagersError(null);
    try {
      const res = await phappsRequest(API_PATHS.GROUP_MANAGERS_LIST, "GET");
      const list = extractArray(res, [
        "managers",
        "groupManagers",
        "Managers",
        "items",
        "data",
        "results",
      ]);
      setManagers(list);
      setSelectedManager((prev) => {
        if (!prev) return null;
        const id = managerId(prev);
        const next = list.find((m) => managerId(m) === id);
        return next || null;
      });
    } catch (e) {
      setManagersError(e.message || "Failed to load managers.");
      setManagers([]);
    } finally {
      setManagersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadManagers();
  }, [loadManagers]);

  const loadManagerChemists = useCallback(async (mgr) => {
    const id = managerId(mgr);
    if (id == null) return;
    setDetailLoading(true);
    setDetailError(null);
    setLinkedSearch("");
    setLinkedSelected(new Set());
    try {
      const [linkedRes, allRes] = await Promise.all([
        phappsRequest(buildGroupManagerLinkedChemistsPath(id), "GET"),
        phappsRequest(API_PATHS.GROUP_MANAGERS_ALL_CHEMISTS, "GET"),
      ]);
      const linked = extractArray(linkedRes, [
        "chemists",
        "linkedChemists",
        "items",
        "data",
        "results",
      ]);
      const all = extractArray(allRes, [
        "chemists",
        "items",
        "data",
        "results",
      ]);
      setLinkedChemists(linked);
      setAllChemists(all);
    } catch (e) {
      setDetailError(e.message || "Failed to load chemist data.");
      setLinkedChemists([]);
      setAllChemists([]);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedManager) {
      setLinkedChemists([]);
      setAllChemists([]);
      setDetailError(null);
      return;
    }
    loadManagerChemists(selectedManager);
  }, [selectedManager, loadManagerChemists]);

  const linkedIds = useMemo(() => {
    const s = new Set();
    for (const r of linkedChemists) {
      const n = Number(chemistRowId(r));
      if (Number.isFinite(n)) s.add(n);
    }
    return s;
  }, [linkedChemists]);

  const filteredLinked = useMemo(
    () => filterChemistRows(linkedChemists, linkedSearch),
    [linkedChemists, linkedSearch]
  );

  const availableToAdd = useMemo(() => {
    return allChemists.filter((c) => {
      const cid = chemistRowId(c);
      if (cid == null) return false;
      return !linkedIds.has(Number(cid));
    });
  }, [allChemists, linkedIds]);

  const toggleInSet = (set, id, on) => {
    const next = new Set(set);
    if (on) next.add(id);
    else next.delete(id);
    return next;
  };

  const selectAllVisibleLinked = () => {
    const ids = filteredLinked
      .map((r) => chemistRowId(r))
      .map(Number)
      .filter((n) => Number.isFinite(n));
    setLinkedSelected(new Set(ids));
  };

  const handleAddChemists = async (chemistIds) => {
    const mid = managerId(selectedManager);
    if (mid == null || !chemistIds?.length) return;
    setAddSaving(true);
    try {
      const body = {
        ManagerID: mid,
        ChemistIDs: chemistIds.filter(Number.isFinite),
      };
      const res = await phappsRequest(
        API_PATHS.GROUP_MANAGERS_ADD_CHEMISTS,
        "POST",
        body
      );
      const n = chemistIds.length;
      setAddChemistsOpen(false);
      await loadManagerChemists(selectedManager);
      await loadManagers();
      const msg =
        res?.addedCount != null
          ? `Added ${res.addedCount} chemist(s).`
          : `Added ${n} chemist(s).`;
      showAlert({ severity: "success", title: "Success", message: msg });
    } catch (e) {
      showAlert({
        severity: "error",
        title: "Error",
        message: e.message || "Add failed.",
      });
    } finally {
      setAddSaving(false);
    }
  };

  const handleRemoveChemists = async () => {
    const mid = managerId(selectedManager);
    if (mid == null || linkedSelected.size === 0) return;
    setRemoveSaving(true);
    try {
      const body = {
        ManagerID: mid,
        ChemistIDs: Array.from(linkedSelected).filter(Number.isFinite),
      };
      const res = await phappsRequest(
        API_PATHS.GROUP_MANAGERS_REMOVE_CHEMISTS,
        "POST",
        body
      );
      const n = linkedSelected.size;
      setLinkedSelected(new Set());
      await loadManagerChemists(selectedManager);
      await loadManagers();
      const msg =
        res?.removedCount != null
          ? `Removed ${res.removedCount} chemist(s).`
          : `Removed ${n} chemist(s).`;
      showAlert({ severity: "success", title: "Success", message: msg });
    } catch (e) {
      showAlert({
        severity: "error",
        title: "Error",
        message: e.message || "Remove failed.",
      });
    } finally {
      setRemoveSaving(false);
    }
  };

  const openEdit = (m, e) => {
    e?.stopPropagation?.();
    setEditTarget(m);
    setEditOpen(true);
  };

  const onCreateSuccess = async (res) => {
    await loadManagers();
    const extra =
      res?.message ||
      res?.inviteMessage ||
      res?.emailSentMessage ||
      (res?.emailSent ? "Invitation email sent." : "");
    showAlert({
      severity: "success",
      title: "Manager created",
      message: extra || "The manager list has been refreshed.",
    });
  };

  const onEditSaved = async () => {
    await loadManagers();
    showAlert({
      severity: "success",
      title: "Saved",
      message: "Manager updated.",
    });
  };

  const selId = selectedManager ? managerId(selectedManager) : null;
  const selectedLabel =
    selectedManager?.Name ||
    selectedManager?.Email ||
    "Selected manager";

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {AlertSnackbar}

      {/* ── Fixed header ── */}
      <div className="flex-shrink-0 px-6 pt-5 pb-4">
        <div className="mb-3">
          <Link
            to="/admin"
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
          >
            ← Admin
          </Link>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Group Managers</h1>
            <p className="text-sm text-gray-600 mt-1 max-w-2xl">
              Create and manage portal group managers, permissions, and linked chemists.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="shrink-0 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
          >
            Create manager
          </button>
        </div>
      </div>

      {/* ── Three-column grid fills remaining height ── */}
      <div className="flex-1 min-h-0 px-6 pb-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full min-w-0">
        <section className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden flex flex-col min-w-0 min-h-0">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-3 shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">Managers</h2>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Show active only
            </label>
          </div>
          <div className="flex-1 min-h-0 overflow-auto">
            {managersError && (
              <div className="p-4 text-sm text-red-700 bg-red-50">{managersError}</div>
            )}
            {managersLoading ? (
              <div className="p-8 text-center text-gray-500 text-sm">Loading…</div>
            ) : visibleManagers.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                {managers.length === 0
                  ? "No group managers yet."
                  : "No managers match this filter."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left">
                  <thead className="bg-gray-100 text-gray-700 text-xs uppercase tracking-wide sticky top-0 z-[1]">
                    <tr>
                      <th className="px-3 py-2 font-medium">Name</th>
                      <th className="px-3 py-2 font-medium">Email</th>
                      <th className="px-3 py-2 font-medium">Comment</th>
                      <th className="px-3 py-2 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {visibleManagers.map((m) => {
                      const id = managerId(m);
                      const activeRow = selId === id;
                      return (
                        <tr
                          key={id}
                          className={`cursor-pointer hover:bg-gray-50 ${
                            activeRow ? "bg-red-50/60" : ""
                          }`}
                          onClick={() => setSelectedManager(m)}
                        >
                          <td className="px-3 py-2 font-medium text-gray-900">
                            {m.Name ?? "—"}
                          </td>
                          <td className="px-3 py-2 text-gray-700 break-all max-w-[140px] lg:max-w-none">
                            {m.Email ?? "—"}
                          </td>
                          <td
                            className="px-3 py-2 text-gray-600 max-w-[120px] lg:max-w-[10rem] truncate"
                            title={m.Comment ?? ""}
                          >
                            {m.Comment ?? "—"}
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <button
                              type="button"
                              onClick={(e) => openEdit(m, e)}
                              className="text-blue-600 hover:underline text-xs font-medium mr-2"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedManager(m);
                              }}
                              className="text-gray-700 hover:underline text-xs font-medium"
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <section className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden flex flex-col min-w-0 min-h-0">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">
              Selected manager
            </h2>
          </div>
          <div className="flex-1 min-h-0 overflow-auto p-4">
            {!selectedManager ? (
              <p className="text-sm text-gray-500 text-center py-8">
                Select a manager from the first column to see details here.
              </p>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => openEdit(selectedManager)}
                    className="text-xs px-2.5 py-1 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-800"
                  >
                    Edit
                  </button>
                </div>
                <div>
                  <p className="text-base font-semibold text-gray-900 leading-snug break-words">
                    {selectedManager.Name || "—"}
                  </p>
                  <p className="text-sm text-gray-600 break-all mt-1">
                    {selectedManager.Email ?? "—"}
                  </p>
                </div>
                {selectedManager.Comment ? (
                  <p className="text-xs text-gray-600 border-t border-gray-100 pt-2 break-words">
                    {selectedManager.Comment}
                  </p>
                ) : null}
                <div className="flex flex-wrap gap-2 text-xs pt-1">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200">
                    PH Admin: <BoolPill value={selectedManager.IsPHAdmin} />
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200">
                    HubMed: <BoolPill value={selectedManager.IsHubMedAdmin} />
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200">
                    Hola: <BoolPill value={selectedManager.IsHolaAdmin} />
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200">
                    Medmate: <BoolPill value={selectedManager.IsMedmateAdmin} />
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200">
                    Zephyr: <BoolPill value={selectedManager.IsZephyrAdmin} />
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200">
                    Active: <BoolPill value={selectedManager.IsActive !== false} />
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Linked chemists: {linkedChemists.length}
                </p>
              </div>
            )}
          </div>
        </section>

        <section className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden flex flex-col min-w-0 min-h-0">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-2 shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">
              Linked chemists
            </h2>
            <button
              type="button"
              onClick={() => setAddChemistsOpen(true)}
              disabled={detailLoading || !selectedManager}
              className="shrink-0 px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              Add chemists
            </button>
          </div>

          <div className="flex-1 min-h-0 flex flex-col p-4 overflow-hidden">
            {!selectedManager ? (
              <p className="text-sm text-gray-500 text-center py-8">
                Select a manager to load linked chemists in this column.
              </p>
            ) : detailError ? (
              <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                {detailError}
              </div>
            ) : detailLoading ? (
              <div className="text-center text-gray-500 text-sm py-10">
                Loading chemists…
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-2 mb-3 shrink-0">
                  <input
                    type="search"
                    placeholder="Filter by name or email…"
                    value={linkedSearch}
                    onChange={(e) => setLinkedSearch(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={selectAllVisibleLinked}
                      className="px-3 py-2 text-xs rounded-md border border-gray-300 bg-white hover:bg-gray-50"
                    >
                      Select all visible
                    </button>
                    <button
                      type="button"
                      onClick={() => setLinkedSelected(new Set())}
                      className="px-3 py-2 text-xs rounded-md border border-gray-300 bg-white hover:bg-gray-50"
                    >
                      Clear selection
                    </button>
                    <button
                      type="button"
                      onClick={handleRemoveChemists}
                      disabled={removeSaving || linkedSelected.size === 0}
                      className="px-3 py-2 text-xs rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      {removeSaving
                        ? "Removing…"
                        : `Remove selected (${linkedSelected.size})`}
                    </button>
                  </div>
                </div>
                <div className="flex-1 min-h-0 border border-gray-200 rounded-lg overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0 text-xs text-gray-700 z-[1]">
                      <tr>
                        <th className="w-10 px-2 py-2" />
                        <th className="px-2 py-2 text-left font-medium">
                          Chemist
                        </th>
                        <th className="px-2 py-2 text-left font-medium">
                          Email
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredLinked.length === 0 ? (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-3 py-8 text-center text-gray-500"
                          >
                            No linked chemists match this filter.
                          </td>
                        </tr>
                      ) : (
                        filteredLinked.map((r) => {
                          const cid = chemistRowId(r);
                          const idn = Number(cid);
                          const valid = Number.isFinite(idn);
                          const checked = valid && linkedSelected.has(idn);
                          return (
                            <tr key={r.LinkID ?? `${cid}-link`}>
                              <td className="px-2 py-1.5">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={!valid}
                                  onChange={(e) =>
                                    valid &&
                                    setLinkedSelected(
                                      toggleInSet(
                                        linkedSelected,
                                        idn,
                                        e.target.checked
                                      )
                                    )
                                  }
                                  className="rounded border-gray-300"
                                />
                              </td>
                              <td className="px-2 py-1.5 text-gray-900">
                                {r.ChemistName ?? r.chemistName ?? "—"}
                              </td>
                              <td className="px-2 py-1.5 text-gray-600 break-all">
                                {r.Email ?? r.email ?? "—"}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
      </div>{/* end flex-1 wrapper */}

      <AddGroupManagerChemistsModal
        isOpen={addChemistsOpen}
        onClose={() => !addSaving && setAddChemistsOpen(false)}
        managerLabel={selectedLabel}
        chemists={availableToAdd}
        saving={addSaving}
        onConfirm={handleAddChemists}
      />

      <CreateGroupManagerModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={onCreateSuccess}
      />
      <EditGroupManagerModal
        isOpen={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditTarget(null);
        }}
        manager={editTarget}
        onSaved={onEditSaved}
      />
    </div>
  );
};

export default GroupManagersPage;
